/**
 * BigInt-safe JSON serialization. JSON.stringify は bigint をそのまま渡すと例外を投げるため
 * replacer で文字列化する。キー順序は入力のまま保持する。
 */
export const jsonStringifyWithBigInt = (obj: unknown): string => {
  return JSON.stringify(obj, (_, value) => (typeof value === 'bigint' ? value.toString() : value))
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false
  // Object.create(null) で生成された prototype: null のオブジェクトも plain として扱う
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/**
 * BigInt 値を含むオブジェクトの deep equality 比較。lodash isEqual の置き換え用途。
 *
 * 設計方針:
 * - 再帰的に hasOwnProperty/Object.keys ベースで比較し、JSON.stringify を介さない
 *   - {a: undefined} と {} を **区別する** (lodash isEqual と同じ)
 *   - 巨大文字列の生成や深い複製を行わず、不一致が見つかった瞬間に短絡する
 * - bigint は型 + 値で厳密比較
 * - plain object のキー順序は無視する
 * - 配列は順序を保持して要素ごとに比較
 * - Date / Map / Set / class インスタンスなどは reference 比較 (本リポジトリの用途範囲では未使用)
 */
export const deepEqualWithBigInt = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true

  // 型不一致は早期 return (null も含む)
  if (typeof a !== typeof b) return false
  if (a === null || b === null) return false

  // primitive (bigint / number / string / boolean / symbol) は Object.is が false の時点で不一致
  if (typeof a !== 'object') return false

  // 配列同士
  const aIsArr = Array.isArray(a)
  const bIsArr = Array.isArray(b)
  if (aIsArr !== bIsArr) return false
  if (aIsArr && bIsArr) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (!deepEqualWithBigInt(a[i], b[i])) return false
    }
    return true
  }

  // plain object 同士 (キー順序無視)
  if (isPlainObject(a) && isPlainObject(b)) {
    const aKeys = Object.keys(a)
    if (aKeys.length !== Object.keys(b).length) return false
    for (const key of aKeys) {
      if (!Object.hasOwn(b, key)) return false
      if (!deepEqualWithBigInt(a[key], b[key])) return false
    }
    return true
  }

  // class インスタンス / Date / Map / Set 等は reference 比較済 (冒頭の Object.is) で同一だった場合のみ true
  return false
}
